/**
 * Card component - polished container with modern styling.
 * Temporary implementation until dads-components exports Card.
 */

import { forwardRef } from 'react'
import { clsx } from 'clsx'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'outlined' | 'elevated' | 'glass'
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'rounded-xl bg-surface-tertiary transition-all duration-200',
          variant === 'default' && 'border border-border-strong/30 shadow-theme-sm',
          variant === 'outlined' && 'border border-border shadow-none',
          variant === 'elevated' && 'border-0 shadow-theme-lg hover:shadow-theme-xl',
          variant === 'glass' &&
            'bg-surface-card backdrop-blur-sm border border-border-card shadow-theme-lg',
          className,
        )}
        {...props}
      />
    )
  },
)

Card.displayName = 'Card'

/**
 * Card header component for consistent section headers.
 */
export const CardHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx('px-7 py-5 border-b border-border-secondary', className)}
      {...props}
    />
  ),
)
CardHeader.displayName = 'CardHeader'

/**
 * Card title component.
 */
export const CardTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    // eslint-disable-next-line jsx-a11y/heading-has-content -- content provided via spread props
    <h3
      ref={ref}
      className={clsx('text-lg font-semibold text-content tracking-tight', className)}
      {...props}
    />
  ),
)
CardTitle.displayName = 'CardTitle'

/**
 * Card description component.
 */
export const CardDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={clsx('text-sm text-content-tertiary mt-1', className)} {...props} />
))
CardDescription.displayName = 'CardDescription'

/**
 * Card content component.
 */
export const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={clsx('p-7', className)} {...props} />,
)
CardContent.displayName = 'CardContent'

/**
 * Card footer component.
 */
export const CardFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx(
        'px-7 py-5 border-t border-border-secondary bg-surface-secondary/50 rounded-b-xl',
        className,
      )}
      {...props}
    />
  ),
)
CardFooter.displayName = 'CardFooter'
